import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiUsers, FiZap, FiMap, FiAward, FiBookOpen, FiExternalLink } from 'react-icons/fi';
import { FaFistRaised } from 'react-icons/fa';
import StoreButtons from '../components/StoreButtons';
import { getProduct } from '../data/catalog';
import Reel from '../components/Reel';

const FeatureCard = ({ icon, title, desc }) => (
  <div className="bg-card border border-border p-6 rounded-none hover:border-accent-red/50 transition-all group">
    <div className="w-12 h-12 rounded-md bg-accent-red/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
      <SafeIcon icon={icon} className="w-6 h-6 text-accent-red" />
    </div>
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-muted text-sm leading-relaxed">{desc}</p>
  </div>
);

const HEROES = [
  { name: 'Erin Walsh', role: 'Ex-detective, fired for asking about VANTAGE. Balanced and precise.' },
  { name: 'Mike "Big Mike" Brennan', role: 'Dock union boss. They took his sister. Slow, unstoppable.' },
  { name: 'Jess Carter', role: 'Street courier who carried the leak. Lightning fast, airborne.' },
  { name: 'Didi & NANA-01', role: 'A baby in a protective mech-cradle. Unlocked after your first full run.' },
];

const IronFury = () => {
  const PRODUCT = getProduct('iron-fury');

  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-red/10 border border-accent-red/20 text-accent-red font-mono text-xs uppercase tracking-[0.16em] mb-6">
              <SafeIcon icon={FaFistRaised} /> Neon-noir beat 'em up · 1-2 players
            </div>
            <h1 className="text-5xl md:text-8xl font-black text-white mb-6 tracking-tighter uppercase italic">
              Iron <span className="text-accent-red">Fury</span>
            </h1>
            <p className="text-xl text-muted max-w-3xl mx-auto mb-10 leading-relaxed font-light italic">
              "One rainy night. Four unlikely heroes. One city to save."
            </p>
            <div className="flex flex-col items-center justify-center gap-5">
              <StoreButtons stores={PRODUCT.stores} accentColor="bg-ember hover:bg-ember-bright" className="justify-center" />
              <a href={PRODUCT.siteLink} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 text-sm font-medium text-muted hover:text-white transition-colors">
                ironfury.mediaverselabs.com <SafeIcon icon={FiExternalLink} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pitch */}
      <section className="py-12 border-y border-border bg-card/20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-lg text-primary leading-relaxed">
            Greyhaven, one night of endless rain. VANTAGE has chipped the city with HALO, and at 06:00 the BLACKOUT
            signal will make every citizen obey. Three ex-allies and a baby in a mech-cradle have one night to rescue
            Dr. Glence Brennan and stop the signal. A finished side-scrolling brawler made for the couch: two friends,
            one controller each.
          </p>
        </div>
      </section>

      {/* Trailer */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">Trailer — Iron Fury</p>
          <div className="border border-border bg-black">
            <video controls preload="metadata" playsInline poster="/reel/iron-fury-1.webp" className="w-full block">
              <source src="https://ironfury.mediaverselabs.com/assets/trailer.mp4" type="video/mp4" />
            </video>
          </div>
          <p className="text-xs text-muted mt-3">All footage is real gameplay.</p>
        </div>
      </section>

      {/* Reel */}
      <section className="pb-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">Reel — Iron Fury</p>
          <Reel frames={PRODUCT.frames} label="Iron Fury" sound={false} ratio="aspect-video" />
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4 uppercase tracking-tight">Game Features</h2>
            <p className="text-muted">The arcade brawlers we grew up with, finished and polished.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard icon={FiMap} title="6 Stages + 2 Set Pieces" desc="Docks, a neon arcade, an overpass, a foundry, a corporate tower and the blackout core, plus a fight on a speeding truck and a glass-lift battle." />
            <FeatureCard icon={FiAward} title="7 Boss Fights" desc="Every district has its commander, ending in a two-phase finale." />
            <FeatureCard icon={FiUsers} title="1-2 Player Co-op" desc="Drop in and out. Team attacks only two players can pull off: hurl your partner like a human cannonball." />
            <FeatureCard icon={FaFistRaised} title="Real Brawling" desc="Combos, grabs and throws, stomps, weapons, destructible scenery, support strikes and a last-resort special." />
            <FeatureCard icon={FiBookOpen} title="A Story to Finish" desc="Told in painted comic panels, with a full ending and a secret hero to unlock." />
            <FeatureCard icon={FiZap} title="Made for Everyone" desc="32 achievements, three difficulties, free play, full remapping, comfort options, English and Spanish." />
          </div>
        </div>
      </section>

      {/* Heroes + specs */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-20 items-start">
            <div className="space-y-6">
              <h2 className="text-4xl font-black text-white uppercase italic">The <span className="text-accent-red">Heroes</span></h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {HEROES.map((h) => (
                  <div key={h.name} className="p-4 bg-card border border-border rounded-md">
                    <h4 className="text-white font-bold mb-1">{h.name}</h4>
                    <p className="text-xs text-muted">{h.role}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-card border border-border rounded-none p-8">
              <h3 className="text-xl font-bold text-white mb-6 uppercase">Details</h3>
              <div className="space-y-4 text-sm">
                {[
                  ['Platform', 'Windows (Steam Deck friendly 16:10)'],
                  ['Players', '1-2 local co-op'],
                  ['Controls', 'Keyboard and gamepad, fully remappable'],
                  ['Languages', 'English, Spanish'],
                  ['Price', PRODUCT.priceNote],
                  ['Status', 'Out now on Epic and Microsoft Store'],
                ].map(([k, v]) => (
                  <div key={k} className="flex justify-between py-2 border-b border-border/50">
                    <span className="text-muted">{k}</span>
                    <span className="text-white text-right">{v}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8">
                <StoreButtons stores={PRODUCT.stores} accentColor="bg-ember hover:bg-ember-bright" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 relative overflow-hidden text-center">
        <div className="absolute inset-0 bg-accent-red/5 -z-10" />
        <div className="max-w-3xl mx-auto px-4">
          <SafeIcon icon={FaFistRaised} className="w-16 h-16 text-accent-red mx-auto mb-8" />
          <h2 className="text-5xl font-black text-white mb-6 uppercase italic">Stop the blackout before dawn</h2>
          <p className="text-muted mb-10 text-lg">
            One-time purchase. No microtransactions. No online requirement. Out now on the Epic Games Store and the Microsoft Store, coming to Steam.
          </p>
          <a href={PRODUCT.siteLink} target="_blank" rel="noopener noreferrer"
             className="btn-primary bg-ember hover:bg-ember-bright text-white px-10 py-4 inline-flex decoration-transparent">
            Visit the IRON FURY site
          </a>
        </div>
      </section>
    </div>
  );
};

export default IronFury;
